import QRCode from 'qrcode';

import { CANVAS_LINE_HEIGHT, CANVAS_LONG_WIDTH, CANVAS_SHORT_WIDTH } from 'src/features/payment/utils/constants';
import { ReceiptFont } from 'src/features/payment/utils/receiptFont';
import { ReceiptLayout } from 'src/features/payment/utils/receiptLayout';

import type { ReceiptRow } from 'src/features/payment/utils/receiptRow';

const FONT_SIZE = 12;
const INK = '#111827';
const BASELINE = 25;

/**
 * What to draw: structured rows where they exist, flat text otherwise.
 *
 * An online payment's receipt comes from the cloud's fiscal note and carries
 * rows, print attributes and the NRA QR. Everything else comes from the on-prem
 * core's financial document, which is a single string - handled by the text path
 * exactly as before.
 */
type ReceiptDrawing = {
    lines?: string[];
    rows?: ReceiptRow[];
    charsPerLine?: number;
};

/**
 * The NRA verification code, drawn module by module.
 *
 * QRCode.create is synchronous and returns the bit matrix, so the square goes
 * straight onto the receipt canvas. Modules are whole pixels: a QR scaled by
 * interpolation may not scan.
 */
const drawQr = (
    ctx: CanvasRenderingContext2D,
    payload: string,
    canvasWidth: number,
    top: number,
    boxHeight: number,
) => {
    let matrix;
    try {
        matrix = QRCode.create(payload, { errorCorrectionLevel: ReceiptLayout.QR_ERROR_CORRECTION }).modules;
    } catch {
        // A payload the encoder dislikes must not take the whole receipt with it.
        return;
    }

    const modules = matrix.size;
    const moduleSize = Math.max(Math.floor(boxHeight / modules), 1);
    const size = moduleSize * modules;
    const left = (canvasWidth - size) / 2;

    // Text is drawn at a BASELINE offset into its line box while the QR gets the
    // raw box, which leaves it touching the row above and a wide gap below. The
    // difference is 2*BASELINE - ascent - CANVAS_LINE_HEIGHT; shift by half of it.
    ctx.font = `${FONT_SIZE}px ${ReceiptFont.STACK}`;
    const ascent = ctx.measureText('M').actualBoundingBoxAscent || FONT_SIZE * 0.75;
    const baselineShift = BASELINE - (ascent + CANVAS_LINE_HEIGHT) / 2;
    const offsetTop = Math.max(top + (boxHeight - size) / 2 + baselineShift, top);

    ctx.fillStyle = 'white';
    ctx.fillRect(left, offsetTop, size, size);
    ctx.fillStyle = 'black';
    for (let y = 0; y < modules; y++) {
        for (let x = 0; x < modules; x++) {
            if (matrix.get(x, y)) {
                ctx.fillRect(left + x * moduleSize, offsetTop + y * moduleSize, moduleSize, moduleSize);
            }
        }
    }
    ctx.fillStyle = INK;
};

/** Canvas size in logical pixels, for whichever form the receipt takes. */
export const receiptCanvasSize = ({ lines, rows, charsPerLine }: ReceiptDrawing) => {
    const structured = rows && rows.length > 0 ? rows : undefined;

    const columns = structured ? ReceiptLayout.columnsOf(structured, charsPerLine) : 0;
    const longest = structured ? columns : (lines?.reduce((acc, line) => Math.max(acc, line.length), 0) ?? 0);
    const lineCount = structured
        ? structured.reduce((total, row) => total + ReceiptLayout.heightScale(row), 0)
        : (lines?.length ?? 0);

    return {
        width: longest > 40 ? CANVAS_LONG_WIDTH : CANVAS_SHORT_WIDTH,
        height: CANVAS_LINE_HEIGHT * lineCount + CANVAS_LINE_HEIGHT,
        columns,
        structured,
    };
};

/**
 * Pixels drawn per CSS pixel.
 *
 * The receipt is laid out in small units - a 12px font on an 18px line - and the
 * canvas was sized in exactly those, so on any HiDPI screen the browser stretched
 * one canvas pixel across two device pixels and the whole receipt looked
 * pixelated. Drawing at this multiple and letting CSS scale it back down makes it
 * sharp, and gives the downloaded JPEG a usable resolution as a bonus.
 */
export const CANVAS_SCALE = 3;

export const canvasDisplaySpecifics = (canvas: HTMLCanvasElement, drawing: ReceiptDrawing) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height, columns, structured } = receiptCanvasSize(drawing);
    if (!structured && !drawing.lines) return;

    canvas.width = width * CANVAS_SCALE;
    canvas.height = height * CANVAS_SCALE;
    // Every coordinate below stays in logical units; this maps them onto the
    // larger backing store. Row-level ctx.scale() calls multiply on top of it and
    // are undone by their own restore().
    ctx.setTransform(CANVAS_SCALE, 0, 0, CANVAS_SCALE, 0, 0);

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = INK;
    ctx.textAlign = 'center';

    if (!structured) {
        // The on-prem financial document is one flat string with no layout to
        // read, so each line is simply trimmed and centred, exactly as before.
        ctx.textBaseline = 'top';
        // The stack carries a monospace fallback: without one a browser that
        // cannot resolve the family falls back to its default serif, and a
        // receipt in Times loses the fixed advance width the layout needs.
        ctx.font = `${FONT_SIZE}px ${ReceiptFont.STACK}`;
        drawing.lines?.forEach((line, index) => {
            ctx.fillText(line.trim(), width / 2, BASELINE + index * CANVAS_LINE_HEIGHT);
        });
        return;
    }

    // A fiscal note carries its layout in the row attributes, so the lines are
    // padded and positioned rather than trimmed and centred individually.
    ctx.textBaseline = 'alphabetic';

    let line = 0;
    for (const row of structured) {
        const rowHeight = ReceiptLayout.heightScale(row);

        const payload = ReceiptLayout.qrPayload(row);
        if (payload) {
            drawQr(ctx, payload, width, line * CANVAS_LINE_HEIGHT, rowHeight * CANVAS_LINE_HEIGHT);
            line += rowHeight;
            continue;
        }

        const rowWidth = ReceiptLayout.widthScale(row);
        const weight = ReceiptLayout.has(row, ReceiptLayout.BOLD) ? 'bold ' : '';

        ctx.save();
        // Scaling the context rather than picking a bigger font keeps a
        // double-width row twice as wide at the SAME height.
        ctx.scale(rowWidth, rowHeight);
        ctx.font = `${weight}${FONT_SIZE}px ${ReceiptFont.STACK}`;
        // A taller row grows upward from its baseline, so the baseline sits on the
        // LAST line it occupies - otherwise it overlaps the row above.
        const baseline = BASELINE + (line + rowHeight - 1) * CANVAS_LINE_HEIGHT;
        ctx.fillText(ReceiptLayout.lineFor(row, columns), width / 2 / rowWidth, baseline / rowHeight);
        ctx.restore();

        line += rowHeight;
    }
};

export const generateCanvas = (drawing: ReceiptDrawing, isLoadedFont: boolean): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    if (!canvas.getContext('2d') || !isLoadedFont) return canvas;

    canvasDisplaySpecifics(canvas, drawing);
    return canvas;
};

export const downloadReceipt = (canvas: HTMLCanvasElement, fileName: string) => {
    if (!canvas) return;
    const url = canvas.toDataURL('image/jpeg');
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.jpg`;
    link.click();
};

export type { ReceiptDrawing };
